import React, { useState } from "react";
import {
  Button,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import EmptyPage from "components/common/EmptyPage";
import RevaText from "components/common/RevaText";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import GestionTable from "components/pages/Gestion/GestionTable";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { leagueCustomerFinancialTransactionsSummaryRoute } from "network/api-routes";
import { formatFinancialAmount } from "./financial-helpers";
import {
  paymentMethodLabel,
  transactionStatusLabel,
  transactionUserLabel,
} from "./transaction-details";
import { useFinancialPagedList } from "./useFinancialPagedList";

const TransactionDocuments = ({ transaction, intl }) => {
  if (transaction.void_reason) {
    return (
      <RevaText variant="body2" sx={{ maxWidth: 260 }}>
        {intl.formatMessage(
          { id: "gestion.financials.voidReason" },
          { reason: transaction.void_reason },
        )}
      </RevaText>
    );
  }
  const links = [
    transaction.proof_url && {
      href: transaction.proof_url,
      id: "gestion.financials.proof",
    },
    transaction.receipt_url && {
      href: transaction.receipt_url,
      id: "gestion.financials.receipt",
    },
  ].filter(Boolean);
  if (links.length === 0) return "—";
  return (
    <Stack direction="row" spacing={1}>
      {links.map((link) => (
        <Link
          key={link.id}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
        >
          {intl.formatMessage({ id: link.id })}
        </Link>
      ))}
    </Stack>
  );
};

const GestionFinancialTransactions = ({
  customerId,
  user,
  reloadKey,
  onError,
}) => {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [showMigrated, setShowMigrated] = useState(false);

  // Migrated-history filtering and search happen server-side.
  const transactions = useFinancialPagedList({
    customerId,
    user,
    route: leagueCustomerFinancialTransactionsSummaryRoute,
    search,
    filters: { include_migrated: showMigrated ? 1 : 0 },
    initialPageSize: 25,
    reloadKey,
    onError,
  });
  const migratedCount = transactions.raw?.migrated_count ?? 0;

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
      >
        <TextField
          size="small"
          label={intl.formatMessage({ id: "gestion.financials.search" })}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ maxWidth: 360, flex: 1 }}
        />
        {migratedCount > 0 && (
          <FormControlLabel
            control={
              <Switch
                checked={showMigrated}
                onChange={(event) => setShowMigrated(event.target.checked)}
              />
            }
            label={intl.formatMessage(
              { id: "gestion.financials.showMigrated" },
              { count: migratedCount },
            )}
          />
        )}
      </Stack>

      {transactions.totalResults === 0 ? (
        transactions.loading ? null : (
          <EmptyPage
            text={intl.formatMessage({
              id: "gestion.financials.noTransactions",
            })}
          />
        )
      ) : (
        <>
          <GestionTable
            headers={[
              {
                key: "date",
                title: intl.formatMessage({ id: "gestion.financials.date" }),
              },
              {
                key: "description",
                title: intl.formatMessage({
                  id: "gestion.financials.description",
                }),
              },
              {
                key: "account",
                title: intl.formatMessage({
                  id: "gestion.financials.accountName",
                }),
              },
              {
                key: "userLabel",
                title: intl.formatMessage({ id: "gestion.financials.user" }),
              },
              {
                key: "method",
                title: intl.formatMessage({
                  id: "gestion.financials.paymentMethod",
                }),
              },
              {
                key: "documents",
                title: intl.formatMessage({
                  id: "gestion.financials.documents",
                }),
                renderCell: (_value, transaction) => (
                  <TransactionDocuments transaction={transaction} intl={intl} />
                ),
              },
              {
                key: "status",
                title: intl.formatMessage({ id: "gestion.financials.status" }),
              },
              {
                key: "amount",
                title: intl.formatMessage({ id: "gestion.financials.amount" }),
                align: "right",
              },
              {
                key: "detail",
                title: "",
                renderCell: (_value, transaction) =>
                  transaction.payment_id ? (
                    <Button
                      size="small"
                      component={RouterLink}
                      to={GESTION_ROUTES.financialPayment(
                        customerId,
                        transaction.payment_id,
                      )}
                    >
                      {intl.formatMessage({
                        id: "gestion.financials.paymentDetail",
                      })}
                    </Button>
                  ) : null,
              },
            ]}
            rows={transactions.results.map((transaction) => ({
              ...transaction,
              date: transaction.date_and_time
                ? dayjs(transaction.date_and_time).format("DD/MM/YYYY HH:mm")
                : "—",
              description: transaction.transaction_name || "—",
              account:
                transaction.origin_info?.name ||
                transaction.origin_info?.id ||
                "—",
              userLabel: transactionUserLabel(transaction.user, intl),
              method: paymentMethodLabel(transaction.payment_method, intl),
              status: transactionStatusLabel(transaction.status, intl),
              amount: formatFinancialAmount(
                transaction.amount,
                transaction.currency,
              ),
            }))}
          />
          <GestionListPagination
            page={transactions.page}
            pageSize={transactions.pageSize}
            totalPages={transactions.totalPages}
            totalResults={transactions.totalResults}
            onPageChange={transactions.setPage}
            onPageSizeChange={transactions.setPageSize}
            disabled={transactions.loading}
          />
        </>
      )}
    </Stack>
  );
};

export default GestionFinancialTransactions;
